import React from 'react';
import { Globe, MessageSquare, Mail, Phone, MessageCircle, Wifi } from 'lucide-react';
import { QRCodeData, QRHistoryItem } from '../types/qr-types';
import { t } from '../i18n/translations';
import FacebookForm from './forms/FacebookForm';
import TwitterForm from './forms/TwitterForm';

interface QRInputFormProps {
  qrData: QRCodeData;
  onDataChange: (data: QRHistoryItem) => void;
  language: 'en' | 'zh';
}

const QRInputForm: React.FC<QRInputFormProps> = ({ qrData, onDataChange, language }) => {
  const handleDataChange = (newData: Partial<QRCodeData>) => {
    const updatedData = { ...qrData, ...newData } as QRCodeData;
    const content = getContentFromData(updatedData);
    const historyItem: QRHistoryItem = {
      ...updatedData,
      dataUrl: '',
      timestamp: Date.now(),
      content
    };
    onDataChange(historyItem);
  };

  const getContentFromData = (data: QRCodeData): string => {
    switch (data.type) {
      case 'url':
        return data.url;
      case 'text':
        return data.text;
      case 'email':
        return `mailto:${data.email}${data.subject ? `?subject=${encodeURIComponent(data.subject)}` : ''}${data.body ? `&body=${encodeURIComponent(data.body)}` : ''}`;
      case 'phone':
        return `tel:${data.phone}`;
      case 'sms':
        return `sms:${data.phone}${data.message ? `?body=${encodeURIComponent(data.message)}` : ''}`;
      case 'wifi':
        return `WIFI:S:${data.ssid};T:${data.encryption};P:${data.password};${data.hidden ? 'H:true;' : ''};`;
      case 'facebook':
        return data.url;
      case 'twitter':
        if (data.twitterType === 'profile') {
          return data.username ? `https://twitter.com/${data.username}` : '';
        } else {
          return data.tweetText ? `https://twitter.com/intent/tweet?text=${encodeURIComponent(data.tweetText)}` : '';
        }
      default:
        return '';
    }
  };

  const renderInput = () => {
    switch (qrData.type) {
      case 'url':
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                {t('websiteUrl', language)}
              </label>
              <div className="relative">
                <Globe className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
                <input
                  type="url"
                  value={qrData.url ?? ''}
                  onChange={(e) => handleDataChange({ url: e.target.value })}
                  placeholder={t('placeholderUrl', language)}
                  className="w-full pl-10 pr-4 py-3 bg-white/5 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              {qrData.url && !validateUrl(qrData.url) && (
                <p className="mt-2 text-sm text-red-400">{t('websiteUrlError', language)}</p>
              )}
            </div>
          </div>
        );

      case 'text':
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                {t('textContent', language)}
              </label>
              <div className="relative">
                <MessageSquare className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
                <textarea
                  value={qrData.text ?? ''}
                  onChange={(e) => handleDataChange({ text: e.target.value })}
                  placeholder={t('placeholderText', language)}
                  rows={4}
                  className="w-full pl-10 pr-4 py-3 bg-white/5 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                />
              </div>
            </div>
          </div>
        );

      case 'email':
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                {t('emailAddress', language)}
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
                <input
                  type="email"
                  value={qrData.email ?? ''}
                  onChange={(e) => handleDataChange({ email: e.target.value })}
                  placeholder={t('placeholderEmail', language)}
                  className="w-full pl-10 pr-4 py-3 bg-white/5 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              {qrData.email && !validateEmail(qrData.email) && (
                <p className="mt-2 text-sm text-red-400">{t('emailAddressError', language) || 'Please enter a valid email address'}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                {t('subject', language)}
              </label>
              <input
                type="text"
                className="w-full px-4 py-3 bg-white/5 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                value={qrData.subject ?? ''}
                onChange={(e) => handleDataChange({ subject: e.target.value })}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                {t('message', language)}
              </label>
              <textarea
                rows={3}
                className="w-full px-4 py-3 bg-white/5 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                value={qrData.body ?? ''}
                onChange={(e) => handleDataChange({ body: e.target.value })}
              />
            </div>
          </div>
        );

      case 'phone':
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                {t('phoneNumber', language)}
              </label>
              <div className="relative">
                <Phone className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
                <input
                  type="tel"
                  value={qrData.phone ?? ''}
                  onChange={(e) => handleDataChange({ phone: e.target.value })}
                  placeholder={t('placeholderPhone', language)}
                  className="w-full pl-10 pr-4 py-3 bg-white/5 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              {qrData.phone && !validatePhone(qrData.phone) && (
                <p className="mt-2 text-sm text-red-400">{t('phoneNumberError', language) || 'Please enter a valid phone number'}</p>
              )}
            </div>
          </div>
        );

      case 'sms':
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                {t('phoneNumber', language)}
              </label>
              <div className="relative">
                <MessageCircle className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
                <input
                  type="tel"
                  value={qrData.phone ?? ''}
                  onChange={(e) => handleDataChange({ phone: e.target.value })}
                  placeholder={t('placeholderPhone', language)}
                  className="w-full pl-10 pr-4 py-3 bg-white/5 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              {qrData.phone && !validatePhone(qrData.phone) && (
                <p className="mt-2 text-sm text-red-400">{t('phoneNumberError', language) || 'Please enter a valid phone number'}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                {t('message', language)}
              </label>
              <textarea
                placeholder={t('smsMessage', language)}
                rows={3}
                className="w-full px-4 py-3 bg-white/5 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                value={qrData.message ?? ''}
                onChange={(e) => handleDataChange({ message: e.target.value })}
              />
            </div>
          </div>
        );

      case 'wifi':
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                {t('networkName', language)}
              </label>
              <div className="relative">
                <Wifi className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  value={qrData.ssid ?? ''}
                  onChange={(e) => handleDataChange({ ssid: e.target.value })}
                  placeholder={t('placeholderWifi', language)}
                  className="w-full pl-10 pr-4 py-3 bg-white/5 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                {t('password', language)}
              </label>
              <input
                type="password"
                value={qrData.password ?? ''}
                onChange={(e) => handleDataChange({ password: e.target.value })}
                placeholder={t('wifiPassword', language)}
                className="w-full px-4 py-3 bg-white/5 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                {t('securityType', language)}
              </label>
              <select
                className="w-full px-4 py-3 bg-white/5 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                value={qrData.encryption ?? 'WPA'}
                onChange={(e) => handleDataChange({ encryption: e.target.value as 'WPA' | 'WEP' | 'nopass' })}
              >
                <option value="WPA">WPA/WPA2</option>
                <option value="WEP">WEP</option>
                <option value="nopass">{t('noPassword', language)}</option>
              </select>
            </div>
            <div>
              <label className="flex items-center gap-2 text-sm font-medium text-gray-300">
                <input
                  type="checkbox"
                  checked={qrData.hidden ?? false}
                  onChange={(e) => handleDataChange({ hidden: e.target.checked })}
                  className="w-4 h-4 rounded border-white/20 bg-white/5 text-blue-500 focus:ring-blue-500"
                />
                {t('hiddenNetwork', language)}
              </label>
            </div>
          </div>
        );

      case 'facebook':
        return (
          <FacebookForm
            data={qrData}
            onChange={handleDataChange}
            language={language}
          />
        );

      case 'twitter':
        return (
          <TwitterForm
            data={qrData}
            onChange={handleDataChange}
            language={language}
          />
        );

      default:
        return null;
    }
  };

  // 判断当前数据是否已准备好生成二维码
  const isReady = (() => {
    switch (qrData.type) {
      case 'url':
        return !!qrData.url && validateUrl(qrData.url);
      case 'text':
        return !!qrData.text;
      case 'email':
        return !!qrData.email;
      case 'phone':
        return !!qrData.phone;
      case 'sms':
        return !!qrData.phone;
      case 'wifi':
        return !!qrData.ssid;
      case 'facebook':
        return !!qrData.url;
      case 'twitter':
        return qrData.twitterType === 'profile'
          ? !!qrData.username
          : !!qrData.tweetText;
      default:
        return false;
    }
  })();

  // Website URL 错误提示
  function validateUrl(url: string): boolean {
    try {
      new URL(url);
      return /^https?:\/\//.test(url);
    } catch {
      return false;
    }
  }

  // Email 校验
  function validateEmail(email: string): boolean {
    return /^[\w.-]+@[\w.-]+\.[a-zA-Z]{2,}$/.test(email);
  }

  // Phone 校验
  function validatePhone(phone: string): boolean {
    return /^\+?\d{7,15}$/.test(phone);
  }

  return (
    <div className="space-y-6">
      {renderInput()}
      {isReady && (
        <div className="p-4 bg-green-500/10 border border-green-500/30 rounded-lg">
          <div className="flex items-center gap-2 text-green-400 text-sm">
            <div className="w-2 h-2 bg-green-400 rounded-full"></div>
            {t('readyToGenerate', language)}
          </div>
        </div>
      )}
    </div>
  );
};

export default QRInputForm;